import type { UseFormReturn } from "react-hook-form";
import { Building2, ClipboardCheck } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { fullSchema } from "./form-validation/full-form.schema";
import type { ProviderOnboardingFormInputs } from "./form-validation/full-form.schema";
import { ONBOARDING_STEP } from "./form-validation/shared";
import { Stepper } from "@shared-kernel/primary/shared/stepper/stepper";
import { useStepperForm } from "@shared-kernel/primary/shared/stepper/use-stepper-form";
import { Button } from "@shared-kernel/primary/shared/shadcn/ui/button";
import type { FormSubmit } from "@shared-kernel/primary/shared/api-errors/action-form";
import { Form } from "@shared-kernel/primary/shared/shadcn/ui/form";
import { Separator } from "@shared-kernel/primary/shared/shadcn/ui/separator";
import { FormStep } from "./subforms/provider-onboarding.form.subform";
import { RecapStep } from "./subforms/provider-onboarding.recap.subform";

interface BodyProps {
  onSubmit: FormSubmit;
  submitting: boolean;
}

interface Props extends BodyProps {
  form: UseFormReturn<ProviderOnboardingFormInputs>;
}

const STEP_SCHEMAS = {
  [ONBOARDING_STEP.FORM]: fullSchema,
} as const;

interface StepComponentProps {
  onEditStep: (stepId: number) => void;
}

type StepDefinition = {
  id: number;
  label: string;
  title: string;
  Icon: LucideIcon;
  Component: React.ComponentType<StepComponentProps>;
};

const BASE_STEPS: StepDefinition[] = [
  { id: ONBOARDING_STEP.FORM, label: "Informations", title: "Votre organisation", Icon: Building2, Component: FormStep },
  { id: ONBOARDING_STEP.RECAP, label: "Récap", title: "Récapitulatif", Icon: ClipboardCheck, Component: RecapStep },
];

const STEP_IDS = BASE_STEPS.map(s => s.id);

export const ProviderOnboarding = ({ form, ...props }: Props) => {
  return (
    <Form {...form}>
      <ProviderOnboardingBody {...props} />
    </Form>
  );
};

const ProviderOnboardingBody = ({ onSubmit, submitting }: BodyProps) => {
  const { currentStepIndex, goNext, goBack, goToStep, isLastStep } = useStepperForm<ProviderOnboardingFormInputs>({
    stepIds: STEP_IDS,
    stepSchemas: STEP_SCHEMAS,
  });

  const currentStep = BASE_STEPS[currentStepIndex];

  if (!currentStep) {
    return <div>Étape non trouvée</div>;
  }

  const { Component: CurrentStepComponent } = currentStep;

  return (
    <div className="space-y-6">
      <Stepper steps={BASE_STEPS} currentStep={currentStepIndex} onStepClick={goToStep} />
      <Separator />
      <form
        onSubmit={
          isLastStep
            ? onSubmit
            : e => {
                e.preventDefault();
                void goNext();
              }
        }
        className="space-y-4"
      >
        <CurrentStepComponent onEditStep={goToStep} />

        <div className="flex justify-between pt-4">
          {currentStepIndex > 0 ? (
            <Button onClick={goBack} type="button" variant="outline">
              Retour
            </Button>
          ) : (
            <span />
          )}
          <Button type="submit" disabled={isLastStep && submitting}>
            {isLastStep ? "Confirmer et finaliser" : "Suivant"}
          </Button>
        </div>
      </form>
    </div>
  );
};
